import React, { useCallback, useEffect, useRef, useState } from 'react';
import Header from 'components/dialogs/DialogHeader';
import Button from 'components/Button';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormLabel,
  FormHelperText,
  OutlinedInput,
  Typography,
} from '@mui/material';
import AccessTime from '@mui/icons-material/AccessTime';
import { useTranslation } from 'react-i18next';
import { useTwoFactorExpiryCountdown } from 'hooks/useTwoFactorExpiryCountdown';
import TwoFactorRememberBrowserSelect from 'components/TwoFactorRememberBrowserSelect';
import { shouldShowRememberBrowserSelect } from 'utils/twoFactorSkipPolicy';

const CODE_LENGTH = 6;
const SUBMIT_MODE_AUTO = 'auto';
const SUBMIT_MODE_MANUAL = 'manual';

function resolveAutoSubmit({
  isOpen,
  code,
  previousCode,
  hadCodeError,
  submitMode,
  allowAutoSubmitOnce,
  isSubmitting,
  lastAutoSubmittedCode,
}) {
  if (!isOpen || code.length === 0) {
    return {
      shouldSubmit: false,
      nextPreviousCode: '',
      nextSubmitMode: SUBMIT_MODE_AUTO,
      nextAllowAutoSubmitOnce: false,
      nextLastAutoSubmittedCode: null,
    };
  }

  let nextSubmitMode = submitMode;

  const editedAfterError =
    hadCodeError &&
    previousCode.length === CODE_LENGTH &&
    (code.length < CODE_LENGTH || (code.length === CODE_LENGTH && previousCode !== code)) &&
    !allowAutoSubmitOnce;

  if (editedAfterError) {
    nextSubmitMode = SUBMIT_MODE_MANUAL;
  }

  if (code.length < CODE_LENGTH) {
    return {
      shouldSubmit: false,
      nextPreviousCode: code,
      nextSubmitMode,
      nextAllowAutoSubmitOnce: allowAutoSubmitOnce,
      nextLastAutoSubmittedCode: null,
    };
  }

  const canAutoSubmit = allowAutoSubmitOnce || nextSubmitMode === SUBMIT_MODE_AUTO;
  const isDuplicateSubmit = lastAutoSubmittedCode === code;

  if (!canAutoSubmit || isSubmitting || isDuplicateSubmit) {
    return {
      shouldSubmit: false,
      nextPreviousCode: code,
      nextSubmitMode,
      nextAllowAutoSubmitOnce: allowAutoSubmitOnce,
      nextLastAutoSubmittedCode: lastAutoSubmittedCode,
    };
  }

  return {
    shouldSubmit: true,
    nextPreviousCode: code,
    nextSubmitMode,
    nextAllowAutoSubmitOnce: false,
    nextLastAutoSubmittedCode: code,
  };
}

function TwoFactorDialog({
  errors = new Map(),
  isOpen,
  isSubmitting,
  maskedEmail,
  codeExpiresAt,
  twoFactorSkipPolicy = null,
  onClose,
  onExpired,
  onSubmit,
  onResend,
  canResend = false,
  isResending = false,
  resendSecondsLeft = 0,
}) {
  const [codeDigits, setCodeDigits] = useState(Array(CODE_LENGTH).fill(''));
  const [remember2faDays, setRemember2faDays] = useState(null);
  const inputRefs = useRef([]);
  const lastAutoSubmittedCodeRef = useRef(null);
  const hadCodeErrorRef = useRef(false);
  const submitModeRef = useRef(SUBMIT_MODE_AUTO);
  const previousCodeRef = useRef('');
  const allowAutoSubmitOnceRef = useRef(false);
  const { t } = useTranslation();

  useEffect(() => {
    setRemember2faDays(null);
  }, [twoFactorSkipPolicy, codeExpiresAt, isOpen]);

  const submitWithRemember = useCallback(
    (twoFactorCode) => {
      const payload = { twoFactorCode };
      if (shouldShowRememberBrowserSelect(twoFactorSkipPolicy) && remember2faDays !== null) {
        payload.remember2faDays = remember2faDays;
      }
      onSubmit(payload);
    },
    [onSubmit, remember2faDays, twoFactorSkipPolicy],
  );

  const { remainingLabel } = useTwoFactorExpiryCountdown(codeExpiresAt, {
    enabled: Boolean(isOpen && codeExpiresAt),
    onExpired,
  });

  const code = codeDigits.join('');
  const normalizedCode = code.replace(/\D/g, '');

  useEffect(() => {
    if (errors.has('code')) {
      hadCodeErrorRef.current = true;
    }
  }, [errors]);

  useEffect(() => {
    const result = resolveAutoSubmit({
      isOpen,
      code: normalizedCode,
      previousCode: previousCodeRef.current,
      hadCodeError: hadCodeErrorRef.current,
      submitMode: submitModeRef.current,
      allowAutoSubmitOnce: allowAutoSubmitOnceRef.current,
      isSubmitting,
      lastAutoSubmittedCode: lastAutoSubmittedCodeRef.current,
    });

    previousCodeRef.current = result.nextPreviousCode;
    submitModeRef.current = result.nextSubmitMode;
    allowAutoSubmitOnceRef.current = result.nextAllowAutoSubmitOnce;
    lastAutoSubmittedCodeRef.current = result.nextLastAutoSubmittedCode;

    if (result.shouldSubmit) {
      submitWithRemember(normalizedCode);
    }
  }, [isOpen, normalizedCode, isSubmitting, submitWithRemember]);

  const focusInput = (index, { shouldSelect = false } = {}) => {
    if (index >= 0 && index < CODE_LENGTH) {
      inputRefs.current[index]?.focus();
      if (shouldSelect) {
        inputRefs.current[index]?.select?.();
      }
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      focusInput(0, { shouldSelect: true });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isOpen]);

  const applyDigits = (digitsValue) => {
    const nextDigits = Array(CODE_LENGTH).fill('');
    digitsValue
      .slice(0, CODE_LENGTH)
      .split('')
      .forEach((digit, index) => {
        nextDigits[index] = digit;
      });

    setCodeDigits(nextDigits);

    const nextFocusIndex = Math.min(digitsValue.length, CODE_LENGTH - 1);
    focusInput(nextFocusIndex);
  };

  const handleDigitChange = (index, value) => {
    const numeric = value.replace(/\D/g, '');

    if (numeric.length > 1) {
      applyDigits(numeric);
      return;
    }

    const nextDigits = [...codeDigits];
    nextDigits[index] = numeric;
    setCodeDigits(nextDigits);

    if (numeric && index < CODE_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !codeDigits[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pastedDigits = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pastedDigits) {
      return;
    }

    if (pastedDigits.length >= CODE_LENGTH) {
      allowAutoSubmitOnceRef.current = true;
      submitModeRef.current = SUBMIT_MODE_AUTO;
    }

    applyDigits(pastedDigits);
  };

  const handleClose = () => {
    onClose();
    setCodeDigits(Array(CODE_LENGTH).fill(''));
    lastAutoSubmittedCodeRef.current = null;
    hadCodeErrorRef.current = false;
    submitModeRef.current = SUBMIT_MODE_AUTO;
    previousCodeRef.current = '';
    allowAutoSubmitOnceRef.current = false;
  };

  useEffect(() => {
    if (!isOpen) {
      setCodeDigits(Array(CODE_LENGTH).fill(''));
      lastAutoSubmittedCodeRef.current = null;
      hadCodeErrorRef.current = false;
      submitModeRef.current = SUBMIT_MODE_AUTO;
      previousCodeRef.current = '';
      allowAutoSubmitOnceRef.current = false;
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} data-testid="two-factor-dialog">
      <Header onClose={handleClose}>{t('auth.login.two-factor-title')}</Header>
      <DialogContent>
        <Typography variant="body2" color="secondary" sx={{ marginBottom: 2 }}>
          {t('auth.login.two-factor-description')}
        </Typography>
        {maskedEmail ? (
          <Typography
            data-testid="two-factor-dialog-masked-email"
            variant="body2"
            color="secondary"
            sx={{ marginBottom: 2, lineHeight: 1.71 }}
          >
            {t('auth.login.two-factor-sent-to', { email: maskedEmail })}
          </Typography>
        ) : null}
        <FormControl error={errors.has('code')} fullWidth sx={{ marginTop: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <FormLabel sx={{ textAlign: 'left' }}>{t('auth.login.two-factor-code-label')}</FormLabel>
              <Box
                sx={{
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  mt: 1,
                }}
              >
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {codeDigits.map((digit, index) => (
                    <OutlinedInput
                      key={`two-factor-dialog-digit-${index}`}
                      value={digit}
                      onChange={(event) => handleDigitChange(index, event.target.value)}
                      onKeyDown={(event) => handleKeyDown(index, event)}
                      onPaste={handlePaste}
                      inputRef={(element) => {
                        inputRefs.current[index] = element;
                      }}
                      inputProps={{
                        maxLength: 1,
                        inputMode: 'numeric',
                        autoComplete: index === 0 ? 'one-time-code' : 'off',
                        'data-testid': index === 0 ? 'two-factor-code-input' : `two-factor-dialog-code-input-${index}`,
                        style: { textAlign: 'center', fontSize: '18px' },
                      }}
                      sx={{
                        width: '52px',
                        height: '52px',
                        borderRadius: '10px',
                        '& .MuiOutlinedInput-input': {
                          padding: 0,
                          height: '100%',
                        },
                      }}
                    />
                  ))}
                </Box>
                {remainingLabel ? (
                  <Box
                    data-testid="two-factor-dialog-countdown"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mt: 0.5,
                    }}
                  >
                    <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} aria-hidden />
                    <Typography variant="body2" color="secondary" sx={{ lineHeight: 1.71 }}>
                      {t('auth.login.two-factor-time-remaining', { time: remainingLabel })}
                    </Typography>
                  </Box>
                ) : null}
                {onResend ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignSelf: 'flex-start',
                      width: '100%',
                      pt: 4,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-start',
                        gap: 0.25,
                        maxWidth: '100%',
                      }}
                    >
                      <Typography variant="body2" color="secondary" component="span" sx={{ lineHeight: 1.71 }}>
                        {t('auth.login.two-factor-resend-prompt')}
                      </Typography>
                      <Button
                        sx={{
                          textTransform: 'none',
                          minWidth: 0,
                          px: 0.25,
                          py: 0.25,
                          fontSize: '0.875rem',
                          fontWeight: 400,
                          lineHeight: 1.71,
                          border: 0,
                          boxShadow: 'none',
                          color: canResend && !isResending ? 'primary.main' : 'text.disabled',
                          '&:hover': {
                            backgroundColor: canResend && !isResending ? 'action.hover' : 'transparent',
                            border: 0,
                            boxShadow: 'none',
                          },
                          '&.Mui-disabled': {
                            color: 'text.disabled',
                            WebkitTextFillColor: 'currentColor',
                            opacity: 1,
                            border: 0,
                            boxShadow: 'none',
                            outline: 'none',
                          },
                          '&.Mui-focusVisible': {
                            boxShadow: 'none',
                          },
                        }}
                        type="button"
                        variant="text"
                        color="inherit"
                        disabled={!canResend || isResending}
                        loading={isResending}
                        onClick={onResend}
                        data-testid="two-factor-resend-button"
                      >
                        {canResend
                          ? t('auth.login.two-factor-resend-code')
                          : t('auth.login.two-factor-resend-wait', { seconds: resendSecondsLeft })}
                      </Button>
                    </Box>
                  </Box>
                ) : null}
              </Box>
              {errors.has('code') ? (
                <FormHelperText
                  data-testid="two-factor-code-error"
                  sx={{ mt: 1, mx: 0, alignSelf: 'stretch' }}
                >
                  {errors.get('code')}
                </FormHelperText>
              ) : null}
            </Box>
          </Box>
        </FormControl>
        <TwoFactorRememberBrowserSelect
          twoFactorSkipPolicy={twoFactorSkipPolicy}
          value={remember2faDays}
          onChange={setRemember2faDays}
        />
      </DialogContent>
      <DialogActions>
        <Button
          sx={{}}
          color="gradient"
          onClick={() => {
            if (normalizedCode.length !== CODE_LENGTH) {
              return;
            }

            submitWithRemember(normalizedCode);
          }}
          disabled={normalizedCode.length !== CODE_LENGTH}
          loading={isSubmitting}
          data-testid="two-factor-submit-button"
        >
          {t('auth.login.two-factor-submit')}
        </Button>
        <Button sx={{ margin: '0 0 0 16px' }} variant="outlined" onClick={handleClose}>
          {t('auth.login.two-factor-back')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TwoFactorDialog;
